/**
 * Analytics helper for GA4 (gtag), consent-gated.
 *
 * Basic consent mode (index.html): until the visitor presses "accept" nothing
 * is measured — no event is queued and gtag.js is never fetched. Accepting
 * loads the library (window.__ankaLoadGtag) and sends the current page view.
 * This module never touches PII — callers MUST NOT pass name, email, phone,
 * company, or message contents in `params`.
 */

/** localStorage key and value shared with index.html and CookieBanner.jsx. */
const CONSENT_STORAGE_KEY = 'cookieConsent'
const CONSENT_ACCEPTED = 'accepted'
const MEASUREMENT_ID = 'G-Z5V3G825ET'

/* This page view's choice, for when storage cannot keep it. */
let sessionChoice = null
/* The page last counted, so a page is never counted twice in a row. */
let lastPageView = null

/**
 * Service identifiers used as the `service` dimension on form events.
 * @readonly
 */
export const SERVICE = {
  padPrinting: 'pad_printing',
  screenPrinting: 'screen_printing',
  assemblyTrim: 'assembly_trim',
  general: 'general',
}

/**
 * Call-to-action identifiers for CTA click events.
 * @readonly
 */
export const CTA = {
  getQuote: 'get_quote',
  viewServices: 'view_services',
}

/**
 * True only for a visitor who accepted analytics cookies and is not excluded
 * (the developer / admin opt-out flag). Unavailable storage means no consent.
 *
 * @returns {boolean}
 */
export function isAnalyticsAllowed() {
  try {
    const stored = localStorage.getItem(CONSENT_STORAGE_KEY)
    return (stored ?? sessionChoice) === CONSENT_ACCEPTED && localStorage.getItem('exclude_analytics') !== 'true'
  } catch {
    return sessionChoice === CONSENT_ACCEPTED
  }
}

/**
 * Apply a consent choice: accepting loads gtag.js and counts this page;
 * declining (also a withdrawal) stops measurement and deletes the _ga cookies.
 *
 * @param {'accepted' | 'rejected'} choice
 * @returns {void}
 */
export function applyConsent(choice) {
  sessionChoice = choice
  const granted = choice === CONSENT_ACCEPTED
  // gtag's own automatic events stop too; the admin opt-out always wins.
  window[`ga-disable-${MEASUREMENT_ID}`] = !granted || isExcluded()
  if (typeof window.gtag === 'function') {
    window.gtag('consent', 'update', { analytics_storage: granted ? 'granted' : 'denied' })
  }
  if (granted) {
    loadAnalytics()
    trackPageView()
    return
  }
  clearAnalyticsCookies()
}

/** @returns {boolean} the developer / admin opt-out flag is set */
function isExcluded() {
  try {
    return localStorage.getItem('exclude_analytics') === 'true'
  } catch {
    return false
  }
}

/** Expire every _ga cookie on this host and its parent domain. */
function clearAnalyticsCookies() {
  const host = window.location.hostname
  const domains = ['', host, `.${host.split('.').slice(-2).join('.')}`]
  document.cookie
    .split(';')
    .map((pair) => pair.split('=')[0].trim())
    .filter((name) => name === '_ga' || name.startsWith('_ga_'))
    .forEach((name) => {
      domains.forEach((domain) => {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${domain ? `; domain=${domain}` : ''}`
      })
    })
}

/**
 * Fire a GA4 event when gtag is available and the visitor has accepted
 * analytics. Never include PII in `params`.
 *
 * @param {string} name - GA4 event name (e.g. 'generate_lead', 'contact_click').
 * @param {Record<string, unknown>} [params] - Non-PII event parameters.
 * @returns {void}
 */
export function trackEvent(name, params) {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return
  if (!isAnalyticsAllowed()) return
  window.gtag('event', name, params)
}

/**
 * Send a page view for the current location (config sends none of its own).
 *
 * @returns {void}
 */
export function trackPageView() {
  const page = window.location.pathname + window.location.search
  if (!isAnalyticsAllowed() || page === lastPageView) return
  lastPageView = page
  trackEvent('page_view', {
    page_location: window.location.href,
    page_path: window.location.pathname + window.location.search,
    page_title: document.title,
  })
}

/**
 * Load gtag.js after the visitor accepts (index.html defines the loader; it
 * does nothing in the prerender or when the library is already in).
 *
 * @returns {void}
 */
export function loadAnalytics() {
  if (typeof window.__ankaLoadGtag === 'function') window.__ankaLoadGtag()
}
